/**
 * Container for non-analytics ("admin") views. Mirrors the
 * ContentShell column shape — `<section bg-[var(--bg)]
 * px-[var(--main-inset)]>` — so the canvas frame, side padding, and
 * tab-strip bottom seam line up with whatever ScopedPage renders.
 *
 * Each view component owns its own `<AdminViewHeader>` so view-
 * specific header actions (column pickers, filters, refresh
 * buttons, …) stay co-located with the view that owns them.
 */
import { ADMIN_VIEWS, permissionForView, allowsView } from "../components/AdminMenu.jsx";
import AdminViewHeader from "../components/AdminViewHeader.jsx";
import { EYEBROW_CLASS } from "../lib/ui.js";
import { useMyPermissions } from "../lib/permissions.js";
import { hasEntitlement } from "../lib/entitlements.js";
import ConnectPage from "./ConnectPage.jsx";
import EndpointInventoryPage from "./EndpointInventoryPage.jsx";
import UnassignedEndpointsPage from "./UnassignedEndpointsPage.jsx";
import ProvisioningTokensPage from "./ProvisioningTokensPage.jsx";
import UserManagementPage from "./UserManagementPage.jsx";
import InstallersPage from "./InstallersPage.jsx";
import ReportPage from "./ReportPage.jsx";
import DirectoryIntegrationsPage from "./DirectoryIntegrationsPage.jsx";
import EndpointConfigurationPage from "./EndpointConfigurationPage.jsx";
import DataSourcesPage from "./DataSourcesPage.jsx";
import AiServicesAdminPage from "./AiServicesAdminPage.jsx";
import AccountPage from "./AccountPage.jsx";
import NotificationsPage from "./NotificationsPage.jsx";
import KernelsPage from "./KernelsPage.jsx";
import CanvasesPage from "./CanvasesPage.jsx";
import WorkflowsPage from "./WorkflowsPage.jsx";
import SignalsPage from "./SignalsPage.jsx";
import SkillsPage from "./SkillsPage.jsx";
import ArtifactRolloutsPage from "./ArtifactRolloutsPage.jsx";
import EscalationsPage from "./EscalationsPage.jsx";
import DiscoveryPage from "./DiscoveryPage.jsx";
import ManagedAgentConfigPage from "./ManagedAgentConfigPage.jsx";

const VIEW_COMPONENTS = {
    "security-overview": DiscoveryPage,
    discovery:  DiscoveryPage,
    alerts:     DiscoveryPage,
    "agent-policy": ManagedAgentConfigPage,
    connect:    ConnectPage,
    kernels:    KernelsPage,
    canvases:   CanvasesPage,
    workflows:  WorkflowsPage,
    signals:    SignalsPage,
    endpoints:  EndpointInventoryPage,
    "unassigned-endpoints": UnassignedEndpointsPage,
    tokens:     ProvisioningTokensPage,
    users:      UserManagementPage,
    directory:  DirectoryIntegrationsPage,
    escalations: EscalationsPage,
    installers: InstallersPage,
    rollouts:   ArtifactRolloutsPage,
    skills:     SkillsPage,
    report:     ReportPage,
    "endpoint-config": EndpointConfigurationPage,
    "data-sources": DataSourcesPage,
    "ai-services": AiServicesAdminPage,
    account: AccountPage,
    notifications: NotificationsPage,
};

function ViewStub({ viewDef }) {
    return (
        <>
            <AdminViewHeader title={viewDef.label} />
            <div className="flex-1 min-h-0 min-w-0 overflow-y-auto">
                <div className="grid h-full place-items-center px-6 py-12">
                    <div className="grid gap-3 text-center max-w-[420px]">
                        <span className={`${EYEBROW_CLASS} text-[color:var(--muted-soft)]`}>
                            {viewDef.label}
                        </span>
                        <h1 className="text-[28px] leading-[1.1] font-medium text-[color:var(--ink)]">
                            {viewDef.label}
                        </h1>
                        <p className="text-[13px] leading-[1.45] text-[color:var(--muted)]">
                            {viewDef.note}. This section is not available yet.
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
}

function ViewRestricted({ viewDef }) {
    return (
        <>
            <AdminViewHeader title={viewDef.label} />
            <div className="flex-1 min-h-0 min-w-0 overflow-y-auto">
                <div className="grid h-full place-items-center px-6 py-12">
                    <div className="grid gap-3 text-center max-w-[420px]">
                        <span className={`${EYEBROW_CLASS} text-[color:var(--muted-soft)]`}>
                            {viewDef.label}
                        </span>
                        <h1 className="text-[28px] leading-[1.1] font-medium text-[color:var(--ink)]">
                            No access
                        </h1>
                        <p className="text-[13px] leading-[1.45] text-[color:var(--muted)]">
                            Your current access does not include {viewDef.label}.
                            Ask a tenant admin to check the required permissions and activity grants.
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
}

function ViewUnknown({ id }) {
    return (
        <>
            <AdminViewHeader title="Unknown view" />
            <div className="flex-1 min-h-0 min-w-0 overflow-y-auto">
                <div className="grid h-full place-items-center px-6 py-12">
                    <div className="grid gap-3 text-center max-w-[420px]">
                        <h1 className="text-[20px] font-medium text-[color:var(--ink)]">
                            Unknown view
                        </h1>
                        <p className="text-[13px] text-[color:var(--muted)]">
                            No admin view registered for <code>{String(id)}</code>.
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
}

export default function AdminPage({ entitlements = null, view, selectedItem = null, onSelectItem = null, onRenameItem = null, onCloseItem = null, focusHeader = null, onScopeNavigate = null }) {
    const def = ADMIN_VIEWS.find((v) => v.id === view);
    const View = VIEW_COMPONENTS[view];
    const caps = useMyPermissions();
    // Mirrors AdminMenu's permission gate — catches the direct-URL case
    // (bookmark, stale link) so someone without the permission doesn't
    // land on a page the backend will 403. `hasPermission` fails open
    // while the set is loading/unavailable, so we never flash "No access"
    // at a permitted user mid-fetch or on a pre-RBAC tenant.
    // Confirmed standing-permission gates, such as Unassigned endpoints,
    // instead stay closed until administration permission is known.
    const restricted = !allowsView(permissionForView(view), caps);
    if (def?.entitlement && !hasEntitlement(entitlements, def.entitlement)) return null;

    return (
        <section className="flex h-full min-h-0 min-w-0 flex-col bg-[color:var(--bg)] px-[var(--main-inset)]">
            {def == null
                ? <ViewUnknown id={view} />
                : restricted
                    ? <ViewRestricted viewDef={def} />
                    : View
                        ? <View entitlements={entitlements} startWithOverview={view === "security-overview"} startWithInvestigations={view === "alerts"} viewDef={def} selectedItem={selectedItem} onSelectItem={onSelectItem} onRenameItem={onRenameItem} onCloseItem={onCloseItem} focusHeader={focusHeader} onScopeNavigate={onScopeNavigate} />
                        : <ViewStub viewDef={def} />}
        </section>
    );
}
